import React, { useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, ArrowUpDown, X, Database } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import { useAppStore } from '../store';
import ExpressStockModal from '../components/modals/ExpressStockModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';
import BulkStockWizard from '../components/modals/BulkStockWizard';

const ExpressStock: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [stockToDelete, setStockToDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showBulkWizard, setShowBulkWizard] = useState(false);
  
  // Filters
  const [productTypeFilter, setProductTypeFilter] = useState<string>('all');
  const [colorFilter, setColorFilter] = useState<string>('all');
  const [sizeFilter, setSizeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'asc' | 'desc'>('desc');
  
  const { expressStock, deleteExpressStock } = useAppStore();

  // Get unique values for filters
  const productTypes = useMemo(() => 
    [...new Set(expressStock.map(stock => stock.product_type))],
    [expressStock]
  );

  const colors = useMemo(() => 
    [...new Set(expressStock.map(stock => stock.color))],
    [expressStock]
  );

  const sizes = ['S', 'M', 'L', 'XL'];

  // Calculate product type totals
  const productTypeTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    expressStock.forEach(stock => {
      if (!totals[stock.product_type]) {
        totals[stock.product_type] = 0;
      }
      totals[stock.product_type] += stock.quantity;
    });
    return totals;
  }, [expressStock]);

  // Filter and sort stock
  const filteredStock = useMemo(() => {
    return expressStock
      .filter(stock => 
        (productTypeFilter === 'all' || stock.product_type === productTypeFilter) &&
        (colorFilter === 'all' || stock.color === colorFilter) &&
        (sizeFilter === 'all' || stock.size === sizeFilter)
      )
      .sort((a, b) => {
        if (sortBy === 'asc') {
          return a.quantity - b.quantity;
        }
        return b.quantity - a.quantity;
      });
  }, [expressStock, productTypeFilter, colorFilter, sizeFilter, sortBy]);

  const handleEdit = (id: string) => {
    setSelectedStock(id);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    setStockToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!stockToDelete) return;
    try {
      await deleteExpressStock(stockToDelete);
      setDeleteConfirmOpen(false);
      setStockToDelete(null);
    } catch (error) {
      console.error('Error deleting stock:', error);
      setError('Error al eliminar el stock');
      setShowErrorModal(true);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setProductTypeFilter('all');
    setColorFilter('all');
    setSizeFilter('all');
  };

  // Function to get stock color based on quantity
  const getStockColor = (quantity: number) => {
    if (quantity === 0) return 'text-red-600 dark:text-red-400';
    if (quantity <= 5) return 'text-yellow-600 dark:text-yellow-400';
    if (quantity <= 15) return 'text-orange-600 dark:text-orange-400';
    return 'text-black dark:text-white';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Stock Express</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={<Database className="h-5 w-5" />}
            onClick={() => setShowBulkWizard(true)}
          >
            Editor Masivo
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={() => {
              setSelectedStock(null);
              setIsModalOpen(true);
            }}
          >
            Crear Item
          </Button>
        </div>
      </div>

      {/* Product Type Totals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(productTypeTotals).map(([type, total]) => (
          <Card key={type}>
            <div className="p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{type}</h3>
              <p className={`text-3xl font-bold mt-2 ${getStockColor(total)}`}>{total} unidades</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader title="Filtros" />
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-40">
              <Select
                label="Tipo"
                value={productTypeFilter}
                onChange={setProductTypeFilter}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...productTypes.map(type => ({ value: type, label: type }))
                ]}
              />
            </div>
            <div className="w-40">
              <Select
                label="Color"
                value={colorFilter}
                onChange={setColorFilter}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...colors.map(color => ({ value: color, label: color }))
                ]}
              />
            </div>
            <div className="w-40">
              <Select
                label="Talle"
                value={sizeFilter}
                onChange={setSizeFilter}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...sizes.map(size => ({ value: size, label: size }))
                ]}
              />
            </div>
            <div className="flex items-center">
              <Button
                variant="outline"
                onClick={() => setSortBy(prev => prev === 'asc' ? 'desc' : 'asc')}
                icon={<ArrowUpDown className="h-4 w-4" />}
                className="h-12 px-3"
              >
                Ordenar por cantidad {sortBy === 'asc' ? '↑' : '↓'}
              </Button>
            </div>
            <div className="flex items-center">
              <Button
                variant="outline"
                onClick={resetFilters}
                icon={<X className="h-4 w-4" />}
                className="h-12 px-3"
              >
                Limpiar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Color
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Talle
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cantidad
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredStock.map((stock) => (
                <tr key={stock.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {stock.product_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {stock.color}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {stock.size}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-right ${getStockColor(stock.quantity)}`}>
                    {stock.quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Edit2 className="h-4 w-4" />}
                        onClick={() => handleEdit(stock.id)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Trash2 className="h-4 w-4" />}
                        onClick={() => handleDelete(stock.id)}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredStock.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No se encontraron resultados con los filtros seleccionados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <ExpressStockModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedStock(null);
        }}
        stockId={selectedStock}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setStockToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Eliminar Stock"
        message="¿Estás seguro de que deseas eliminar este stock? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />

      <BulkStockWizard
        isOpen={showBulkWizard}
        onClose={() => setShowBulkWizard(false)}
      />
    </div>
  );
};

export default ExpressStock;